import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import React, { useState } from 'react'

import type { Sentiment } from '../api/types'
import { textAnalyticsBus } from '../bus/textAnalyticsBus'

type MonthOption = { value: string; label: string }
type SentimentOption = { value: Sentiment; label: string; color: string }

type Props = {
	blockId: number
	applyUrl: string
	resetUrl: string
	months: MonthOption[]
	sentiments: SentimentOption[]
	initialSelectedMonths: string[]
	initialSelectedSentiment: Sentiment | null
	resetLabel: string
	monthPlaceholder: string
}

type LinkedStatesResponse = { linkedStates: Array<Parameters<typeof textAnalyticsBus.publish>[0]> }

const post = async (url: string, body: unknown): Promise<void> => {
	try {
		const res = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body),
		})
		const data = (await res.json()) as LinkedStatesResponse
		data.linkedStates.forEach((state) => textAnalyticsBus.publish(state))
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error('[WorkspaceFilters] request failed', err)
	}
}

export const WorkspaceFilters: React.FC<Props> = ({
	blockId,
	applyUrl,
	resetUrl,
	months,
	sentiments,
	initialSelectedMonths,
	initialSelectedSentiment,
	resetLabel,
	monthPlaceholder,
}) => {
	const [selectedMonths, setSelectedMonths] = useState<string[]>(initialSelectedMonths)
	const [sentiment, setSentiment] = useState<Sentiment | null>(initialSelectedSentiment)

	const apply = (nextSentiment: Sentiment | null, nextMonths: string[]): void => {
		setSentiment(nextSentiment)
		setSelectedMonths(nextMonths)
		void post(applyUrl, { sentiment: nextSentiment, months: nextMonths })
	}

	const toggleSentiment = (value: Sentiment): void => {
		apply(sentiment === value ? null : value, selectedMonths)
	}

	const reset = (): void => {
		setSentiment(null)
		setSelectedMonths([])
		void post(resetUrl, {})
	}

	const selectedMonthOptions = months.filter((month) => selectedMonths.includes(month.value))

	return (
		<div className="flex flex-row items-center justify-end gap-2 p-2" data-block-id={blockId}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				size="small"
				options={months}
				value={selectedMonthOptions}
				getOptionLabel={(option) => option.label}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				onChange={(_event, newValue) =>
					apply(
						sentiment,
						newValue.map((option) => option.value),
					)
				}
				renderOption={(optionProps, option, { selected }) => {
					const { key, ...liProps } = optionProps
					return (
						<li key={key} {...liProps}>
							<Checkbox size="small" style={{ marginRight: 8 }} checked={selected} />
							{option.label}
						</li>
					)
				}}
				renderInput={(params) => <TextField {...params} label={monthPlaceholder} />}
				sx={{ minWidth: 220 }}
			/>

			{sentiments.map((s) => (
				<button
					key={s.value}
					type="button"
					aria-pressed={sentiment === s.value}
					aria-label={s.label}
					title={s.label}
					onClick={() => toggleSentiment(s.value)}
					className="h-6 w-6 rounded-full border"
					style={{
						backgroundColor: s.color,
						outline: sentiment === s.value ? '2px solid #111' : undefined,
					}}
				/>
			))}

			<button type="button" onClick={reset} className="rounded border px-3 py-1 text-sm">
				{resetLabel}
			</button>
		</div>
	)
}
